import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {ActiveChatProgressMessageBubble} from 'llm/chat-library/messages/ChatProgress';
import {ProgressStatus, ClientProgressMessage} from 'llm/chat-library/messages/MessageTypes';
import {ProgressToolCall, getToolIcon, getToolDisplayName} from 'llm/deep-research/tool-details/types';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import {
  GRAY_DARKER,
  BLUE_DARKER,
  BLUE_LIGHTEST,
  GRAY_DARK,
  PURPLE_LIGHTEST,
  RED_LIGHTEST,
} from 'ui-library/Colors';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {useActivityModalControls} from '../ActivityModal';

export interface ProgressData {
  numSources: number;
  historyThread: Array<ProgressToolCall>;
}

export interface DeepResearchProgressMessageProps {
  message: ClientProgressMessage;
}

/**
 * A progress component for deep research.
 */
export function DeepResearchProgressMessage({message}: DeepResearchProgressMessageProps): JSX.Element {
  const {openActivityModal} = useActivityModalControls();
  const {status, errorMessage, progressData} = message;

  // Fallback to the shared progress message bubble if progress data is not present.
  if (!progressData) {
    return <ActiveChatProgressMessageBubble message={message} />;
  }

  switch (status) {
    case ProgressStatus.STARTED:
    case ProgressStatus.IN_PROGRESS:
      return (
        <div
          className={css(styles.progressContainer)}
          {...getTestProps({
            component: 'DeepResearchProgress',
            element: 'root',
          })}
        >
          <ProgressHeader progressData={progressData} />
          <ProgressActivity progressData={progressData} status={status} />
          {errorMessage && (
            <div
              className={css(styles.progressContent)}
              {...getTestProps({
                component: 'DeepResearchProgress',
                element: 'content',
              })}
            >
              <span className={css(styles.errorMessage)}>{errorMessage}</span>
            </div>
          )}
        </div>
      );
    case ProgressStatus.COMPLETED:
      return <ProgressCompleted progressData={progressData} onOpenProgressModal={openActivityModal} />;
    case ProgressStatus.FAILED:
      return <ProgressFailed errorMessage={errorMessage || 'Could not complete deep research'} />;
    default:
      throw new Error(`Unknown progress status: ${status}`);
  }
}

export function ProgressFailed({errorMessage}: {errorMessage: string}): JSX.Element {
  return (
    <div
      className={css(styles.progressFailedContainer)}
      {...getTestProps({
        component: 'DeepResearchProgress',
        element: 'root',
      })}
    >
      <div className={css(styles.progressHeader)}>
        <span>Deep research failed</span>
      </div>
      <div
        className={css(styles.progressContent)}
        {...getTestProps({
          component: 'DeepResearchProgress',
          element: 'content',
        })}
      >
        <span className={css(styles.errorMessage)}>{errorMessage}</span>
      </div>
    </div>
  );
}

export interface ProgressCompletedProps {
  progressData: ProgressData;
  onOpenProgressModal: (progressData: ProgressData) => void;
}

export function ProgressCompleted({progressData, onOpenProgressModal}: ProgressCompletedProps): JSX.Element {
  const {numSources} = progressData;

  const handleOpenModal = (): void => {
    onOpenProgressModal(progressData);
  };

  return (
    <div
      className={css(styles.progressHeader, styles.progressCompletedContainer)}
      onClick={handleOpenModal}
      {...getTestProps({component: 'DeepResearchProgress', element: 'root'})}
    >
      <div className={css(styles.completedSection)}>
        <span className={css(styles.completedText)}>
          Research completed
          {numSources > 0 ? ` • ${numSources} ${pluralize(numSources, 'source', 'sources')}` : ''}
        </span>
        <BenchlingIcon icon="chevron-right" />
      </div>
    </div>
  );
}

export interface ProgressDataProps {
  progressData: ProgressData;
}

export function ProgressHeader({progressData}: ProgressDataProps): JSX.Element {
  return (
    <div className={css(styles.progressHeader)}>
      <div
        className={css(styles.thinkingSection)}
        {...getTestProps({
          component: 'DeepResearchProgress',
          element: 'thinkingSection',
        })}
      >
        <BenchlingDuotoneIcon
          icon="spinner-third"
          primaryColor={BLUE_DARKER}
          secondaryColor={BLUE_LIGHTEST}
          spin={true}
        />
        <span className={css(styles.thinkingText)}>Thinking...</span>
      </div>
      {progressData && progressData.numSources > 0 && (
        <div className={css(styles.sourcesText)}>
          {progressData.numSources} {pluralize(progressData.numSources, 'source', 'sources')}
        </div>
      )}
    </div>
  );
}

export interface ProgressActivityProps {
  progressData: ProgressData;
  status: ProgressStatus;
  onToolClick?: (toolCall: ProgressToolCall) => void;
}

export function ProgressActivity({progressData, status, onToolClick}: ProgressActivityProps): JSX.Element {
  return (
    <div
      className={css(styles.progressContent)}
      {...getTestProps({
        component: 'DeepResearchProgress',
        element: 'content',
      })}
    >
      {status === ProgressStatus.STARTED ? (
        <div
          className={css(styles.emptyActivityItem)}
          {...getTestProps({
            component: 'DeepResearchProgress',
            element: 'started',
          })}
        >
          <span className={css(styles.toolDescription)}>Starting deep research.</span>
          <span>This could take a few minutes.</span>
        </div>
      ) : status === ProgressStatus.IN_PROGRESS && progressData.historyThread.length === 0 ? (
        <div className={css(styles.emptyActivityItem)}>
          <span className={css(styles.toolDescription)}>Analyzing your question...</span>
        </div>
      ) : (
        progressData.historyThread.map((toolCall, index) => (
          <div
            {...getTestProps({component: 'DeepResearchProgress', element: 'activityItem', key: `${index}`})}
            key={index}
            className={css(styles.activityItem, onToolClick && styles.activityItemClickable)}
            onClick={
              onToolClick
                ? () => {
                    onToolClick(toolCall);
                  }
                : undefined
            }
          >
            <BenchlingIcon icon={getToolIcon(toolCall.tool)} className={css(styles.activityIcon)} />
            <div className={css(styles.activityDescription)}>
              <span
                className={css(styles.toolDescription)}
                {...getTestProps({
                  component: 'DeepResearchProgress',
                  element: 'toolDescription',
                  key: `${index}`,
                })}
              >
                {getToolDisplayName(toolCall.tool)}
              </span>
              {/* TODO (BNCH-126793): Add tool summarized tool calls to frontend */}
              {/* {toolCall.description} */}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  // Progress container
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '8px',
    backgroundColor: PURPLE_LIGHTEST,
  },

  progressCompletedContainer: {
    cursor: 'pointer',
    padding: '16px',
    borderRadius: '4px',
    backgroundColor: PURPLE_LIGHTEST,
  },

  progressFailedContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '4px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    backgroundColor: RED_LIGHTEST,
  },

  // Progress Header
  progressHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '8px',
  },

  thinkingSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '20px',
  },

  thinkingText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },

  sourcesText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARK,
  },

  // Completed
  completedSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },

  completedText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },

  // Error
  errorMessage: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },

  // Progress Activity
  progressContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: GRAY_DARK,
  },

  activityItemClickable: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
    borderRadius: '4px',
  },

  activityIcon: {
    width: '16px',
    textAlign: 'center',
  },

  toolDescription: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },

  activityDescription: {
    ...FONT_PROXIMA_14,
    fontSize: '13px',
    color: GRAY_DARKER,
    flex: 1,
  },

  emptyActivityItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
});
